import { api } from "@/utils/api";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { loginUser } from "@/features/user/userSlice";
import { useDispatch } from "react-redux";
import { useState, useEffect, useCallback } from "react";
import { toast } from "@/utils/appToast";
import { SetStorage, setAuthentication } from "@/shared/services/credentialsService";
import Checkbox from "@/shared/checkbox/checkbox";
import { initPushNotifications } from "@/utils/pushUtils";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import GoogleSignIn from "@/utils/googleSignIn";
import { isWorkerProfileComplete } from "@/utils/workerProfileUtils";
import { StorageI } from "@/types/interfaces";
import { getSafeRedirectPath } from "@/utils/safeRedirect";
import type { WorkerProfile } from "@/types/userTypes";

type PostLoginPayload = {
    token: string;
    user: any;
    rememberMe: boolean;
    emailForReset?: string;
    workerProfile?: WorkerProfile | null;
    workerProfileIncluded: boolean;
};

const LoginPage = () => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const postLoginRedirect = getSafeRedirectPath(searchParams.get("redirect"));
    const dispatch = useDispatch();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [emailError, setEmailError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleEmailChange = (value: string) => {
        setEmail(value);
        if (!value.trim()) {
            setEmailError("");
            return;
        }
        setEmailError(validateEmail(value) ? "" : t("toastValidEmail"));
    };

    const isFormValid = email.trim() !== "" && password.trim() !== "" && emailError === "";

    const finishPostLogin = useCallback(
        async ({
            token,
            user,
            rememberMe,
            emailForReset,
            workerProfile,
            workerProfileIncluded,
        }: PostLoginPayload) => {
            const storageData: StorageI = {
                data: {
                    token,
                    user,
                },
            };
            SetStorage(storageData, rememberMe);

            if (user?.forcePasswordReset) {
                navigate(
                    `/reset-password?token=${user?._id}&email=${encodeURIComponent(emailForReset || user?.email || "")}`
                );
                return;
            }

            dispatch(loginUser({ token, user }));

            if (!user?.isEmailVerified) {
                toast.error(t("toastUnverifiedEmail"));
                return;
            }

            if (!user?.isEssentialCompleted) {
                setAuthentication(true, rememberMe);
                navigate("/complete-essential-info", {
                    state: {
                        userType: user?.type === "worker" ? "worker" : "client",
                    },
                });
                return;
            }

            setAuthentication(true, rememberMe);

            if (user?.type === "worker") {
                let profile: WorkerProfile | null | undefined = workerProfileIncluded
                    ? workerProfile
                    : undefined;

                if (!workerProfileIncluded) {
                    try {
                        profile = await api.getMyWorkerProfile();
                    } catch {
                        profile = undefined;
                    }
                }

                if (profile && !isWorkerProfileComplete(profile)) {
                    navigate("/settings");
                    toast.success(t("toastLoginSuccessfully"));
                    void initPushNotifications();
                    return;
                }

                navigate(postLoginRedirect || "/my-jobs");
                toast.success(t("toastLoginSuccessfully"));
                void initPushNotifications();
                return;
            }

            navigate(postLoginRedirect || "/");
            toast.success(t("toastLoginSuccessfully"));
            void initPushNotifications();
        },
        [dispatch, navigate, postLoginRedirect, t]
    );

    const handleLogIn = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!validateEmail(email)) {
            toast.error(t("toastValidEmail"));
            return;
        }

        const loginForm = e.target as HTMLFormElement;
        const loginData = new FormData(loginForm);
        const rememberMe = loginData.get("remember-me") === "on";

        try {
            setIsLoading(true);
            const res = await api.signIn({
                email: email,
                password: password,
            });

            if (res && res.status === 403) {
                if (res.message?.includes("deactivated")) {
                    toast.info(t("toastProfileUnderValidation"), {
                        style: {
                            background: "#4285f4",
                            color: "#fff",
                        },
                    });
                } else {
                    toast.error(res?.message);
                }
                setIsLoading(false);
                return;
            }

            if (res && res.success === true) {
                const payload = res.data;
                const workerProfileIncluded = Object.prototype.hasOwnProperty.call(
                    payload,
                    "workerProfile"
                );

                await finishPostLogin({
                    token: payload.token,
                    user: payload.user,
                    rememberMe,
                    emailForReset: email,
                    workerProfile: payload.workerProfile,
                    workerProfileIncluded,
                });
                setIsLoading(false);
            } else {
                toast.error(res.message);
                setIsLoading(false);
            }
        } catch {
            setIsLoading(false);
        }
    };

    const handleGoogleSuccess = useCallback(
        async (payload: {
            token: string;
            user: any;
            workerProfile?: WorkerProfile | null;
            workerProfileIncluded: boolean;
        }) => {
            await finishPostLogin({
                token: payload.token,
                user: payload.user,
                rememberMe: true,
                emailForReset: payload.user?.email,
                workerProfile: payload.workerProfile,
                workerProfileIncluded: payload.workerProfileIncluded,
            });
        },
        [finishPostLogin]
    );

    return (
        <>
            <style>{`
                #remember + label.remember {
                    color: #1e293b !important;
                }
                #remember + label.remember:before {
                    border-color: #1e293b !important;
                    background-color: white !important;
                }
                #remember:checked + label.remember:before {
                    background-color: #4285F4 !important;
                    border-color: #4285F4 !important;
                }
                #remember:checked + label.remember:after {
                    background: white !important;
                    box-shadow: 2px 0 0 white, 4px 0 0 white, 4px -2px 0 white, 4px -4px 0 white, 4px -6px 0 white, 4px -8px 0 white !important;
                }
            `}</style>
            <div
                style={{
                    minHeight: "100vh",
                    backgroundColor: "#f5f5f3",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    padding: "20px",
                    paddingTop: "120px",
                }}
            >
            <div
                style={{
                    backgroundColor: "white",
                    borderRadius: "24px",
                    padding: isMobile ? "28px 24px" : "32px 40px",
                    maxWidth: "500px",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <div style={{ textAlign: "center", marginBottom: "24px" }}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            marginBottom: "12px",
                        }}
                    >
                        <img
                            src="/images/phone_symbol.svg"
                            alt="Phone Symbol"
                            style={{
                                width: '50px',
                                height: '50px',
                                display: 'block'
                            }}
                        />
                    </div>
                    <h1
                        style={{
                            fontSize: isMobile ? "22px" : "26px",
                            fontWeight: 600,
                            color: "#1e293b",
                            margin: 0,
                            lineHeight: "1.3",
                        }}
                    >
                        Jandi - {t('login')}
                    </h1>
                </div>

                <form method="post" onSubmit={handleLogIn}>
                    <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label htmlFor="loginEmail" style={{ color: "#1e293b", display: "block", fontSize: "14px" }}>
                                {t('userEmail')} <span style={{ color: "red" }}> *</span>
                            </label>
                            <input
                                id="loginEmail"
                                type="email"
                                name="email"
                                value={email}
                                onChange={(e) => handleEmailChange(e.target.value)}
                                placeholder={t('email')}
                                style={{ 
                                    borderRadius: "8px", 
                                    border: "1px solid #e5e7eb",
                                    width: "100%",
                                    minHeight: "48px",
                                    padding: "12px 16px",
                                    fontSize: "15px",
                                    boxSizing: "border-box"
                                }}
                            />
                            {emailError && (
                                <p style={{ color: "#dc2626", fontSize: "13px", marginTop: "6px", marginBottom: 0 }}>
                                    {emailError}
                                </p>
                            )}
                        </div>

                        <div className="form-group" style={{ marginBottom: 0, position: "relative" }}>
                            <label htmlFor="loginPassword" style={{ color: "#1e293b", display: "block", fontSize: "14px" }}>
                                {t('password')} <span style={{ color: "red" }}> *</span>
                            </label>
                            <div style={{ position: "relative" }}>
                                <input
                                    id="loginPassword"
                                    style={{ 
                                        width: "100%", 
                                        paddingRight: "45px", 
                                        borderRadius: "8px", 
                                        border: "1px solid #e5e7eb",
                                        minHeight: "48px",
                                        paddingLeft: "16px",
                                        paddingTop: "12px",
                                        paddingBottom: "12px",
                                        fontSize: "15px",
                                        boxSizing: "border-box"
                                    }}
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder={t('password')}
                                />
                                <IconButton
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    style={{
                                        position: "absolute",
                                        right: 5,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        padding: 0,
                                        color: "#64748b",
                                    }}
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "2px" }}>
                                <Checkbox
                                    name="remember-me"
                                    id="remember"
                                    label={t('rememberMe')}
                                    labelClass="remember"
                                />
                                <Link
                                    to="/forgot-password"
                                    style={{
                                        color: "#4285F4",
                                        textDecoration: "none",
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        cursor: "pointer",
                                    }}
                                >
                                    {t('forgotPassword')}
                                </Link>
                            </div>
                        </div>

                        <button
                            className="theme-btn btn-style-one"
                            type="submit"
                            name="log-in"
                            disabled={isLoading || !isFormValid}
                            style={{
                                backgroundColor: isLoading || !isFormValid ? "#94a3b8" : "#4285F4",
                                color: "white",
                                width: "100%",
                                padding: "14px 32px",
                                borderRadius: "12px",
                                border: "none",
                                fontSize: "16px",
                                fontWeight: 600,
                                cursor: isLoading || !isFormValid ? "not-allowed" : "pointer",
                                marginBottom: "2px"
                            }}
                        >
                            {isLoading ? (
                                <div
                                    className="spinner-border"
                                    style={{ height: "15px", width: "15px" }}
                                    role="status"
                                ></div>
                            ) : (
                                t('login')
                            )}
                        </button>

                        <div style={{ textAlign: "center", marginBottom: "2px" }}>
                            <span style={{ color: "#64748b", fontSize: "14px" }}>{t("o")}</span>
                        </div>

                        <div style={{ marginBottom: "2px" }}>
                            <GoogleSignIn onSuccess={handleGoogleSuccess} />
                        </div>

                        <div style={{ textAlign: "center", marginTop: "2px" }}>
                            <span style={{ fontSize: "14px", color: "#64748b" }}>
                                {t("doYouAlreadyHaveAnAccount")?.replace("Login", "").replace("?", "") || "Don't have an account?"}{" "}
                                <Link
                                    to="/registro"
                                    style={{
                                        color: "#4285F4",
                                        textDecoration: "none",
                                        fontWeight: 500,
                                        cursor: "pointer",
                                    }}
                                >
                                    {t("signup")}
                                </Link>
                            </span>
                        </div>
                    </div>
                </form>
            </div>
        </div>
        </>
    );
};

export default LoginPage;
